import { InvestorDetailsGetInvestorLegalEntitiesQuery, PaginateSortDirection, Role } from '@/__generated__/graphql';
import { ISelectValue } from '@/components/basicComponents/select';
import { createSimpleSelectOption } from '@/components/basicComponents/select/utils';
import { ITab } from '@/components/basicComponents/tabs';
import { LoaderOnWholeScreen } from '@/components/loaders/loader-on-whole-screen';
import { PUBLISHED_INVESTMENT_MANAGER_UPDATES_INVESTMENT_IDS_QUERY } from '@/components/managerUpdates-page/queries';
import { useSearchParamsManager } from '@/hooks/use-search-params';
import { useStatusData } from '@/hooks/useStatusData';
import { PaddingWrap } from '@/styles/common';
import { useQuery } from '@apollo/client';
import { LegalEntityGroupingLevel } from '@trace-8/trace8-schema';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Details from './details/details';
import { InvestorDetailsLayout } from './investorDetailsLayout';
import { INVESTOR_DETAILS_LIST_ENTITY_TRANSACTIONS_TOTAL_QUERY, INVESTOR_LEGAL_ENTITIES_QUERY } from './queries';
import { ActivityTab, BeginAllocationTab, CommitmentsTab, DocumentationTab, PricingTab, SummaryTab, UpdatesTab } from './tabs';
import { InvestorDetailsEntityTypeEnum, InvestorDetailsTabsEnum } from './types';

const defaultTabs = [{ value: InvestorDetailsTabsEnum.SUMMARY }, { value: InvestorDetailsTabsEnum.COMMITMENTS }];

const isTabExists = (tabs: ITab[], activeTab: InvestorDetailsTabsEnum): boolean => {
  if (!tabs.length || !activeTab) return false;
  return tabs.some((tab) => tab.value === activeTab);
};

export const InvestorDetails = () => {
  const params = useParams();
  const navigate = useNavigate();
  const { data: statusData } = useStatusData();
  const { searchParams, onChangeActiveTab } = useSearchParamsManager();

  const investorId = params?.investorId || '';
  const entityId = params?.entityId || '';
  const backToTitle = searchParams.get('backToTitle') || '';
  const activeTab: InvestorDetailsTabsEnum = (searchParams.get('activeTab') as InvestorDetailsTabsEnum) || InvestorDetailsTabsEnum.SUMMARY;
  const isGroup = searchParams.get('isGroup') === 'true';

  const [entityType, setEntityType] = useState<InvestorDetailsEntityTypeEnum>(null);
  const [groupId, setGroupId] = useState<string | null>(null);
  const [tabs, setTabs] = useState<ITab[]>(defaultTabs);
  const [advisorName, setAdvisorName] = useState('');
  const [investorName, setInvestorName] = useState('');
  const [selectedGroupName, setSelectedGroupName] = useState<string>('');
  const [entitiesSelectOptions, setEntitiesSelectOptions] = useState<ISelectValue[]>([]);
  const [entityIdsForActivityTab, setEntityIdsForActivityTab] = useState<string[]>([]);

  const getEntityIdsByType = (
    entityType: InvestorDetailsEntityTypeEnum,
    investorLegalEntitiesData: InvestorDetailsGetInvestorLegalEntitiesQuery['getInvestor']['legalEntities'],
    entityId?: string
  ) => {
    switch (entityType) {
      case InvestorDetailsEntityTypeEnum.Household:
        return investorLegalEntitiesData.map((entity) => entity.id);

      case InvestorDetailsEntityTypeEnum.Group:
        return investorLegalEntitiesData.find((entity) => entity.id === entityId)?.entities?.map((entity) => entity.id);

      case InvestorDetailsEntityTypeEnum.GroupedEntity:
        return [entityId];

      default:
        return entityId ? [entityId] : [];
    }
  };

  const { data: publishedInvestmentManagerUpdatesTotalData, loading: publishedInvestmentManagerUpdatesTotalLoading } = useQuery(
    PUBLISHED_INVESTMENT_MANAGER_UPDATES_INVESTMENT_IDS_QUERY,
    {
      notifyOnNetworkStatusChange: true,
      variables: {
        data: {
          familyId: investorId,
          legalEntityGroupingLevel: LegalEntityGroupingLevel.Household,
          publishedUpdatesOnly: true
        }
      }
    }
  );

  const { data: investorLegalEntitiesData, loading: investorLegalEntitiesLoading } = useQuery(INVESTOR_LEGAL_ENTITIES_QUERY, {
    notifyOnNetworkStatusChange: true,
    skip: !investorId,
    variables: {
      data: {
        id: investorId,
        legalEntitiesList: {
          sortBy: 'name',
          sortDirection: PaginateSortDirection.Asc
        }
      }
    },
    onCompleted: (data) => {
      const { name, legalEntities, advisor } = data.getInvestor;
      const selectOptions = legalEntities.flatMap((entity) => {
        if (entity.isGroup && entity.entities) {
          const groupedEntities = entity.entities.map((groupedEntity) => createSimpleSelectOption(groupedEntity.id, `- ${groupedEntity.entityName}`));
          return [createSimpleSelectOption(`group-${entity.id}`, entity.entityName), ...groupedEntities];
        }
        return [createSimpleSelectOption(entity.id, entity.entityName)];
      });

      const hasAdvisorRole =
        advisor?.roles?.includes(Role.AdvisorAssociate) || advisor?.roles?.includes(Role.AdvisorLead) || advisor?.roles?.includes(Role.AdvisorMember);
      const advisorName = hasAdvisorRole && advisor?.firstName && advisor?.lastName ? `${advisor.firstName} ${advisor.lastName}` : '';

      setInvestorName(name);
      setAdvisorName(advisorName);
      setEntitiesSelectOptions(selectOptions);
    },
    onError: (error) => {
      console.error(error);
      navigate('/404', { replace: true });
    }
  });

  const { data: listEntityTransactionsTotalData, loading: listEntityTransactionsTotalLoading } = useQuery(
    INVESTOR_DETAILS_LIST_ENTITY_TRANSACTIONS_TOTAL_QUERY,
    {
      notifyOnNetworkStatusChange: true,
      skip: !entityIdsForActivityTab?.length,
      variables: {
        legalEntityIds: entityIdsForActivityTab,
        page: 1,
        rowsPerPage: 1
      }
    }
  );

  useEffect(() => {
    if (investorLegalEntitiesLoading || !investorLegalEntitiesData) return;

    const topLevelEntity = investorLegalEntitiesData.getInvestor.legalEntities.find((entity) => entity.id === entityId);
    if (topLevelEntity?.isGroup && isGroup) {
      setEntityType(InvestorDetailsEntityTypeEnum.Group);
      setGroupId(topLevelEntity?.groupId || null);
      setEntityIdsForActivityTab(
        getEntityIdsByType(InvestorDetailsEntityTypeEnum.Group, investorLegalEntitiesData?.getInvestor?.legalEntities, entityId)
      );
      return;
    }

    const groupEntity = investorLegalEntitiesData.getInvestor.legalEntities.find(
      (entity) => entity.entities?.some((groupedEntity) => groupedEntity.id === entityId)
    );

    if (groupEntity) {
      setSelectedGroupName(groupEntity.entityName);
      setEntityType(InvestorDetailsEntityTypeEnum.GroupedEntity);
      setGroupId(null);
      setEntityIdsForActivityTab(
        getEntityIdsByType(InvestorDetailsEntityTypeEnum.GroupedEntity, investorLegalEntitiesData?.getInvestor?.legalEntities, entityId)
      );
      return;
    }

    if (topLevelEntity) {
      setEntityType(InvestorDetailsEntityTypeEnum.UngroupedEntity);
      setGroupId(null);
      setEntityIdsForActivityTab(
        getEntityIdsByType(InvestorDetailsEntityTypeEnum.UngroupedEntity, investorLegalEntitiesData?.getInvestor?.legalEntities, entityId)
      );
      return;
    }

    setEntityType(InvestorDetailsEntityTypeEnum.Household);
    setGroupId(null);
    setEntityIdsForActivityTab(
      getEntityIdsByType(InvestorDetailsEntityTypeEnum.Household, investorLegalEntitiesData?.getInvestor?.legalEntities, entityId)
    );
    if (entityId) {
      navigate(`/investors/${investorId}`, { replace: true });
    }
  }, [entityId, isGroup, investorLegalEntitiesLoading, investorLegalEntitiesData]);

  useEffect(() => {
    if (investorLegalEntitiesLoading || listEntityTransactionsTotalLoading || publishedInvestmentManagerUpdatesTotalLoading) return;

    const getTabsByEntityType = (entityType: InvestorDetailsEntityTypeEnum): ITab[] => {
      const baseTabs = [...defaultTabs];
      const activityTabTotal = listEntityTransactionsTotalData?.ListEntityTransactions?.total || 0;
      const publishedInvestmentManagerUpdatesTotal = publishedInvestmentManagerUpdatesTotalData?.publishedInvestmentManagerUpdates?.length || 0;

      switch (entityType) {
        case InvestorDetailsEntityTypeEnum.Household:
          const householdTabs = [
            ...(publishedInvestmentManagerUpdatesTotal > 0 ? [{ value: InvestorDetailsTabsEnum.UPDATES }] : []),
            ...(activityTabTotal > 0 ? [{ value: InvestorDetailsTabsEnum.ACTIVITY }] : [])
          ];
          return [...baseTabs, ...householdTabs];

        case InvestorDetailsEntityTypeEnum.UngroupedEntity:
          const ungroupedTabs = [
            { value: InvestorDetailsTabsEnum.ALLOCATIONS },
            ...(activityTabTotal > 0 ? [{ value: InvestorDetailsTabsEnum.ACTIVITY }] : []),
            { value: InvestorDetailsTabsEnum.PACING },
            ...(statusData.subdoc_enabled ? [{ value: InvestorDetailsTabsEnum.DOCUMENTATION }] : []),
            ...(publishedInvestmentManagerUpdatesTotal > 0 ? [{ value: InvestorDetailsTabsEnum.UPDATES }] : [])
          ];
          return [...baseTabs, ...ungroupedTabs];

        case InvestorDetailsEntityTypeEnum.Group:
          const groupTabs = [
            { value: InvestorDetailsTabsEnum.ALLOCATIONS },
            ...(activityTabTotal > 0 ? [{ value: InvestorDetailsTabsEnum.ACTIVITY }] : []),
            { value: InvestorDetailsTabsEnum.PACING },
            ...(publishedInvestmentManagerUpdatesTotal > 0 ? [{ value: InvestorDetailsTabsEnum.UPDATES }] : [])
          ];
          return [...baseTabs, ...groupTabs];

        case InvestorDetailsEntityTypeEnum.GroupedEntity:
          const groupedTabs = [
            ...(activityTabTotal > 0 ? [{ value: InvestorDetailsTabsEnum.ACTIVITY }] : []),
            ...(statusData.subdoc_enabled ? [{ value: InvestorDetailsTabsEnum.DOCUMENTATION }] : []),
            ...(publishedInvestmentManagerUpdatesTotal > 0 ? [{ value: InvestorDetailsTabsEnum.UPDATES }] : [])
          ];
          return [...baseTabs, ...groupedTabs];

        default:
          return baseTabs;
      }
    };

    const tabs = getTabsByEntityType(entityType);

    if (!isTabExists(tabs, activeTab)) {
      onChangeActiveTab(InvestorDetailsTabsEnum.SUMMARY, '', { backToTitle, isGroup });
    }
    setTabs(tabs);
  }, [
    statusData,
    entityType,
    listEntityTransactionsTotalLoading,
    listEntityTransactionsTotalData,
    publishedInvestmentManagerUpdatesTotalLoading,
    publishedInvestmentManagerUpdatesTotalData
  ]);

  const resetEntityType = () => {
    setEntityType(null);
  };

  if (investorLegalEntitiesLoading) {
    return <LoaderOnWholeScreen />;
  }

  if (
    activeTab === InvestorDetailsTabsEnum.ACTIVITY ||
    activeTab === InvestorDetailsTabsEnum.DOCUMENTATION ||
    activeTab === InvestorDetailsTabsEnum.PACING ||
    activeTab === InvestorDetailsTabsEnum.UPDATES
  ) {
    return (
      <>
        {activeTab === InvestorDetailsTabsEnum.ACTIVITY && (
          <ActivityTab
            entityType={entityType}
            investorData={{
              investorName,
              advisorName,
              legalEntities: investorLegalEntitiesData?.getInvestor?.legalEntities || [],
              legalEntitiesTotalCount: investorLegalEntitiesData?.getInvestor?.legalEntities?.length || 0
            }}
            selectedGroupName={selectedGroupName}
            entitiesSelectOptions={entitiesSelectOptions}
            entityIdsForActivityTab={entityIdsForActivityTab}
            tabs={tabs}
            resetEntityType={resetEntityType}
          />
        )}
        {activeTab === InvestorDetailsTabsEnum.DOCUMENTATION && (
          <DocumentationTab
            entityType={entityType}
            investorData={{
              investorName,
              advisorName,
              legalEntities: investorLegalEntitiesData?.getInvestor?.legalEntities || [],
              legalEntitiesTotalCount: investorLegalEntitiesData?.getInvestor?.legalEntities?.length || 0
            }}
            selectedGroupName={selectedGroupName}
            entitiesSelectOptions={entitiesSelectOptions}
            tabs={tabs}
            resetEntityType={resetEntityType}
          />
        )}
        {activeTab === InvestorDetailsTabsEnum.PACING && (
          <PricingTab
            entityType={entityType}
            investorData={{
              investorName,
              advisorName,
              legalEntities: investorLegalEntitiesData?.getInvestor?.legalEntities || [],
              legalEntitiesTotalCount: investorLegalEntitiesData?.getInvestor?.legalEntities?.length || 0
            }}
            selectedGroupName={selectedGroupName}
            entitiesSelectOptions={entitiesSelectOptions}
            tabs={tabs}
            resetEntityType={resetEntityType}
          />
        )}
        {activeTab === InvestorDetailsTabsEnum.UPDATES && (
          <UpdatesTab
            entityType={entityType}
            investorData={{
              investorName,
              advisorName,
              legalEntities: investorLegalEntitiesData?.getInvestor?.legalEntities || [],
              legalEntitiesTotalCount: investorLegalEntitiesData?.getInvestor?.legalEntities?.length || 0
            }}
            selectedGroupName={selectedGroupName}
            entitiesSelectOptions={entitiesSelectOptions}
            groupId={groupId}
            tabs={tabs}
            resetEntityType={resetEntityType}
          />
        )}
      </>
    );
  }

  return (
    <InvestorDetailsLayout
      entityType={entityType}
      investorData={{
        investorName,
        advisorName,
        legalEntities: investorLegalEntitiesData?.getInvestor?.legalEntities || [],
        legalEntitiesTotalCount: investorLegalEntitiesData?.getInvestor?.legalEntities?.length || 0
      }}
      selectedGroupName={selectedGroupName}
      entitiesSelectOptions={entitiesSelectOptions}
      tabs={tabs}
      resetEntityType={resetEntityType}
    >
      {entityType !== InvestorDetailsEntityTypeEnum.Household && <Details entityType={entityType} />}
      <PaddingWrap>
        {activeTab === InvestorDetailsTabsEnum.SUMMARY && <SummaryTab entityType={entityType} />}
        {activeTab === InvestorDetailsTabsEnum.COMMITMENTS && <CommitmentsTab entityType={entityType} />}
        {activeTab === InvestorDetailsTabsEnum.ALLOCATIONS && <BeginAllocationTab entityType={entityType} />}
      </PaddingWrap>
    </InvestorDetailsLayout>
  );
};
